"use client";

import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { MessageSquare } from "@/components/icons";
import { RepackifyMark } from "@/components/brand/repackify-logo";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { Button } from "@/components/ui/button";
import { LoadFailureDialog } from "@/components/ui/load-failure-dialog";
import { useSearchContextSafe } from "@/components/search/search-context";
import { useActiveViewAs } from "@/components/view-as/use-active-view-as";
import { OPEN_SUPPORT_CHAT_EVENT } from "@/lib/chat/open-public-chat";
import { HIDDEN_BESIDE_HERO_ASK } from "@/components/layout/hero-ask-visibility";
import { cn } from "@/lib/utils";

const PublicAskPanel = lazy(() =>
  import("./public-ask-panel").then((module) => ({
    default: module.PublicAskPanel,
  })),
);


function useAskPanelRootFlag(visible: boolean, threadWanted: boolean) {
  useEffect(() => {
    const root = document.documentElement;
    if (visible) root.dataset.askPanel = "open";
    else if (threadWanted) root.dataset.askPanel = "hidden";
    else delete root.dataset.askPanel;
    return () => {
      delete root.dataset.askPanel;
    };
  }, [visible, threadWanted]);
}

function usePanelState(disabled: boolean) {
  const [chosen, setChosen] = useState<boolean | null>(null);
  const [threadWanted, setThreadWanted] = useState(false);
  const { publicChatMessage, clearPublicChatMessage } = useSearchContextSafe();

  if (!disabled && publicChatMessage && !(threadWanted && chosen === true)) {
    setThreadWanted(true);
    setChosen(true);
  }
  const visible = !disabled && chosen === true;
  useAskPanelRootFlag(visible, threadWanted);

  useEffect(() => {
    if (disabled && publicChatMessage) clearPublicChatMessage();
  }, [disabled, publicChatMessage, clearPublicChatMessage]);

  const show = useCallback(() => setChosen(true), []);
  const hide = useCallback(() => setChosen(false), []);

  useEffect(() => {
    if (disabled) return;
    window.addEventListener(OPEN_SUPPORT_CHAT_EVENT, show);
    return () => window.removeEventListener(OPEN_SUPPORT_CHAT_EVENT, show);
  }, [disabled, show]);

  return { visible, mounted: chosen !== null, requested: chosen === true, threadWanted, show, hide };
}

function DismissOnFailure({ onDismiss }: { onDismiss: () => void }) {
  useEffect(onDismiss, [onDismiss]);
  return null;
}

function AskPanelSlot({ panel }: { panel: ReturnType<typeof usePanelState> }) {
  return (
    <ErrorBoundary
      label="public-ask-panel"
      fallback={
        panel.requested ? (
          <LoadFailureDialog
            title="Ask Repackify could not load"
            open={panel.visible}
            onOpenChange={(open) => {
              if (!open) panel.hide();
            }}
          />
        ) : (
          <DismissOnFailure onDismiss={panel.hide} />
        )
      }
    >
      <Suspense fallback={null}>
        <PublicAskPanel
          visible={panel.visible}
          requested={panel.requested}
          threadWanted={panel.threadWanted}
          onHide={panel.hide}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

export function PublicChatWidget() {
  const viewingAs = useActiveViewAs();
  const panel = usePanelState(Boolean(viewingAs));

  if (viewingAs) return null;

  return (
    <>
      {!panel.visible && (
        <Button variant="chip" size="chip" className={cn("ml-2 shrink-0", HIDDEN_BESIDE_HERO_ASK)} onClick={panel.show}>
          <RepackifyMark alt="" className="h-4" />
          <span className="max-sm:sr-only">Ask Repackify</span>
          <span className="sm:hidden" aria-hidden>
            Ask
          </span>
        </Button>
      )}
      {!panel.visible && panel.threadWanted && (
        <Button
          variant="chip"
          size="chip"
          className="fixed bottom-[max(1rem,var(--safe-area-inset-bottom))] left-1/2 z-40 min-h-12 -translate-x-1/2 whitespace-nowrap bg-surface px-5 text-base shadow-modal lg:hidden"
          onClick={panel.show}
        >
          <MessageSquare className="size-[18px]" aria-hidden />
          Back to chat
        </Button>
      )}
      {panel.mounted && <AskPanelSlot panel={panel} />}
    </>
  );
}
